"use client"

import { useState } from "react"
import { useNavigate } from "react-router-dom"
import {
  FiHome,
  FiUsers,
  FiFileText,
  FiSettings,
  FiHelpCircle,
  FiMessageCircle,
  FiFolder,
  FiBookOpen,
  FiUser,
  FiCalendar,
  FiVideo,
  FiInfo,
  FiShield,
  FiPenTool,
  FiBell,
  FiGlobe,
  FiLogOut,
  FiChevronRight,
  FiAward,
} from "react-icons/fi"
import { useAuth } from "../context/AuthContext"
import { getUserRole } from "../services/Auth/loginService"
import Swal from "sweetalert2"

export default function Sidebar({
  handleHomeClick,
  setShowFoldersView,
  setShowHomeView,
  setShowChatsView,
  setShowHelpView,
  handleNotificationsClick,
  handleLanguageClick,
  handleThemesClick,
  handleAccountInfoClick,
  handlePrivacySecurityClick,
  handleCalendarClick,
  handleMeetingsClick,
  handleCasesClick,
  handleClientManagementClick,
  handleLawyerManagementClick,
}) {
  const navigate = useNavigate()
  const [expandedMenu, setExpandedMenu] = useState(null)
  const { logout } = useAuth()

  // Get user role and convert to lowercase for consistent comparison
  const userRole = getUserRole()?.toLowerCase()

  console.log("Current user role:", userRole) // Debug log to verify role

  const toggleSubMenu = (menu) => {
    setExpandedMenu(expandedMenu === menu ? null : menu)
  }

  const handleLogout = (e) => {
    e.preventDefault()

    Swal.fire({
      title: "¿Estás seguro que deseas cerrar sesión?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, cerrar sesión",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        logout()

        // Remove authentication cookies
        ;["token", "refreshToken", "userData", "accountData"].forEach((cookieName) => {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
        })

        navigate("/")
      }
    })
  }

  // Function to render menu items based on user role
  const renderMenuItems = () => {
    // Define menu items for each role
    const menuConfig = {
      admin: [
        {
          type: "single",
          icon: FiHome,
          text: "Inicio",
          onClick: handleHomeClick,
        },
        {
          type: "submenu",
          icon: FiBookOpen,
          text: "Integraciones",
          submenuItems: [
            {
              icon: FiCalendar,
              text: "Calendario",
              onClick: handleCalendarClick,
            },
            {
              icon: FiVideo,
              text: "Reuniones",
              onClick: handleMeetingsClick,
            },
          ],
        },
        {
          type: "single",
          icon: FiMessageCircle,
          text: "Chats",
          onClick: () => {
            setShowChatsView(true)
            setShowHomeView(false)
            setShowFoldersView(false)
            setShowHelpView(false)
          },
        },
        {
          type: "single",
          icon: FiFolder,
          text: "Carpetas",
          onClick: () => {
            setShowFoldersView(true)
            setShowHomeView(false)
            setShowChatsView(false)
            setShowHelpView(false)
          },
        },
        {
          type: "single",
          icon: FiAward,
          text: "Abogados",
          onClick: (e) => {
            e.preventDefault()
            handleLawyerManagementClick(true)
            setShowHomeView(false)
          },
        },
        {
          type: "single",
          icon: FiUsers,
          text: "Clientes",
          onClick: (e) => {
            e.preventDefault()
            handleClientManagementClick(true)
            setShowHomeView(false)
          },
        },
        {
          type: "single",
          icon: FiFileText,
          text: "Casos",
          onClick: (e) => {
            e.preventDefault()
            handleCasesClick(true)
            setShowHomeView(false)
          },
        },
      ],
      lawyer: [
        {
          type: "single",
          icon: FiHome,
          text: "Inicio",
          onClick: handleHomeClick,
        },
        {
          type: "submenu",
          icon: FiBookOpen,
          text: "Integraciones",
          submenuItems: [
            {
              icon: FiCalendar,
              text: "Calendario",
              onClick: handleCalendarClick,
            },
            {
              icon: FiVideo,
              text: "Reuniones",
              onClick: handleMeetingsClick,
            },
          ],
        },
        {
          type: "single",
          icon: FiMessageCircle,
          text: "Chats",
          onClick: () => {
            setShowChatsView(true)
            setShowHomeView(false)
            setShowFoldersView(false)
            setShowHelpView(false)
          },
        },
        {
          type: "single",
          icon: FiFolder,
          text: "Carpetas",
          onClick: () => {
            setShowFoldersView(true)
            setShowHomeView(false)
            setShowChatsView(false)
            setShowHelpView(false)
          },
        },
        {
          type: "single",
          icon: FiUsers,
          text: "Clientes",
          onClick: (e) => {
            e.preventDefault()
            handleClientManagementClick(true)
            setShowHomeView(false)
          },
        },
        {
          type: "single",
          icon: FiFileText,
          text: "Casos",
          onClick: (e) => {
            e.preventDefault()
            handleCasesClick(true)
            setShowHomeView(false)
          },
        },
      ],
      client: [
        {
          type: "single",
          icon: FiHome,
          text: "Inicio",
          onClick: handleHomeClick,
        },
        {
          type: "submenu",
          icon: FiBookOpen,
          text: "Integraciones",
          submenuItems: [
            {
              icon: FiCalendar,
              text: "Calendario",
              onClick: handleCalendarClick,
            },
            {
              icon: FiVideo,
              text: "Reuniones",
              onClick: handleMeetingsClick,
            },
          ],
        },
        {
          type: "single",
          icon: FiMessageCircle,
          text: "Chats",
          onClick: () => {
            setShowChatsView(true)
            setShowHomeView(false)
            setShowFoldersView(false)
            setShowHelpView(false)
          },
        },
        {
          type: "single",
          icon: FiFileText,
          text: "Casos",
          onClick: (e) => {
            e.preventDefault()
            handleCasesClick(true)
            setShowHomeView(false)
          },
        },
      ],
    }

    // Common menu items for all roles
    const commonMenuItems = [
      {
        type: "submenu",
        icon: FiUser,
        text: "Cuenta",
        submenuItems: [
          {
            icon: FiInfo,
            text: "Información",
            onClick: handleAccountInfoClick,
          },
          {
            icon: FiShield,
            text: "Privacidad y Seguridad",
            onClick: handlePrivacySecurityClick,
          },
        ],
      },
      {
        type: "submenu",
        icon: FiSettings,
        text: "Configuración",
        submenuItems: [
          {
            icon: FiPenTool,
            text: "Temas",
            onClick: handleThemesClick,
          },
          {
            icon: FiBell,
            text: "Notificaciones",
            onClick: handleNotificationsClick,
          },
          {
            icon: FiGlobe,
            text: "Idioma",
            onClick: handleLanguageClick,
          },
        ],
      },
      {
        type: "single",
        icon: FiHelpCircle,
        text: "Ayuda",
        onClick: () => {
          setShowHelpView(true)
          setShowHomeView(false)
          setShowFoldersView(false)
          setShowChatsView(false)
        },
      },
    ]

    // Combine role-specific and common menu items
    // Check if userRole exists in menuConfig, otherwise default to client
    const roleMenuItems = menuConfig[userRole] || menuConfig.client
    const fullMenuItems = [...roleMenuItems, ...commonMenuItems]

    return fullMenuItems.map((item, index) => {
      if (item.type === "single") {
        return (
          <li key={index}>
            <a
              href="#"
              className="flex items-center px-6 py-2 text-navy-600 hover:bg-gray-100"
              onClick={(e) => {
                e.preventDefault()
                item.onClick(e)
              }}
            >
              <item.icon className="h-5 w-5 mr-3" />
              {item.text}
            </a>
          </li>
        )
      } else if (item.type === "submenu") {
        return (
          <li key={index}>
            <button
              onClick={() => toggleSubMenu(item.text)}
              className="flex items-center w-full px-6 py-2 text-navy-600 hover:bg-gray-100 focus:outline-none"
            >
              <item.icon className="h-5 w-5 mr-3" />
              <span className="flex-1 text-left">{item.text}</span>
              <FiChevronRight
                className={`h-4 w-4 transition-transform ${expandedMenu === item.text ? "rotate-90" : ""}`}
              />
            </button>
            {expandedMenu === item.text && (
              <ul className="pl-12 mt-1 space-y-1">
                {item.submenuItems.map((subitem, subindex) => (
                  <li key={subindex}>
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        subitem.onClick(e)
                      }}
                      className="block py-1 text-navy-600 hover:text-navy-900 w-full text-left"
                    >
                      <subitem.icon className="h-4 w-4 inline mr-2" />
                      {subitem.text}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </li>
        )
      }
      return null
    })
  }

  return (
    <>
      <div className="flex items-center justify-between h-16 border-b px-4">
        <div className="flex items-center">
          <span className="text-2xl font-bold text-navy-900 ml-10">Lawfeed</span>
        </div>
      </div>
      <nav className="flex-grow">
        <ul className="space-y-2 py-4">{renderMenuItems()}</ul>
      </nav>
      {/* Cerrar Sesión at the bottom */}
      <div className="p-4 border-t">
        <button
          className="flex items-center w-full px-6 py-2 text-red-600 hover:bg-gray-100 rounded text-left"
          onClick={handleLogout}
        >
          <FiLogOut className="h-5 w-5 mr-3" />
          Cerrar Sesión
        </button>
      </div>
    </>
  )
}

